import { MaskedInput, Select } from "components/inputs";
import debounce from "lodash/debounce";
import { useMemo, useState } from "react";
import type {
  Control,
  FieldErrors,
  UseFormSetValue,
  UseFormWatch,
} from "react-hook-form";
import { Controller } from "react-hook-form";
import { useGetDRGCodesByInputQuery } from "services/codes-api";

import type { SurgicalPricingCptCode } from "./CPTCodeSection";

type DRGCodeOption = {
  label: string;
  value: string;
  code: string;
  description?: string;
};

type BooleanOption = {
  label: string;
  value: boolean;
};

type APCCPTOption = {
  label: string;
  value: number;
};

const booleanOptions: BooleanOption[] = [
  { label: "Yes", value: true },
  { label: "No", value: false },
];

const MAX_DRG_SEARCH_RESULTS = 25;

export interface FacilityFeeFormFields {
  adj_apc_payment: boolean;
  facility_fee_apc_cpt: number | null;
  ms_drg_code: string;
  ms_drg_description: string;
  ms_drg_base_price: number | null;
  cpt_codes: SurgicalPricingCptCode[];
}

type FacilityFeeSectionProps = {
  control: Control<FacilityFeeFormFields>;
  errors: FieldErrors<FacilityFeeFormFields>;
  cptCodes: SurgicalPricingCptCode[];
  setValue: UseFormSetValue<FacilityFeeFormFields>;
  watch: UseFormWatch<FacilityFeeFormFields>;
};

const FacilityFeeSection = ({
  control,
  errors,
  cptCodes,
  setValue,
  watch,
}: FacilityFeeSectionProps) => {
  const [codeSearchTerm, setCodeSearchTerm] = useState<string | null>(null);
  const [descSearchTerm, setDescSearchTerm] = useState<string | null>(null);

  const debouncedSetCodeSearchTerm = useMemo(
    () =>
      debounce((value: string | null) => {
        setCodeSearchTerm(value);
      }, 500),
    [],
  );

  const debouncedSetDescSearchTerm = useMemo(
    () =>
      debounce((value: string | null) => {
        setDescSearchTerm(value);
      }, 500),
    [],
  );

  const { data: codeResults, isFetching: isCodeFetching } =
    useGetDRGCodesByInputQuery(
      {
        code__istartswith: codeSearchTerm?.match(/^\d/) ? codeSearchTerm : null,
        description__icontains: !codeSearchTerm?.match(/^\d/)
          ? codeSearchTerm
          : null,
        limit: MAX_DRG_SEARCH_RESULTS,
      },
      {
        skip: !codeSearchTerm,
      },
    );

  const { data: descResults, isFetching: isDescFetching } =
    useGetDRGCodesByInputQuery(
      {
        description__icontains: descSearchTerm,
        limit: MAX_DRG_SEARCH_RESULTS,
      },
      {
        skip: !descSearchTerm,
      },
    );

  const codeOptions = useMemo(() => {
    if (!codeResults?.results) return [];

    return codeResults.results.map((result: DRGCodeOption) => ({
      label: `${result.code} - ${result.description}`,
      value: result.code,
      code: result.code,
      description: result.description,
    }));
  }, [codeResults]);

  const descOptions = useMemo(() => {
    if (!descResults?.results) return [];

    return descResults.results.map((result: DRGCodeOption) => ({
      label: `${result.code} - ${result.description}`,
      value: result.code,
      code: result.code,
      description: result.description,
    }));
  }, [descResults]);

  const adjApcPayment = watch("adj_apc_payment");

  return (
    <>
      <div className="header-primary grid-2-wide">Facility Fee</div>

      <Controller
        name="adj_apc_payment"
        control={control}
        render={({ field: { onChange, value, ...field } }) => (
          <>
            <Select
              {...field}
              options={booleanOptions}
              value={
                booleanOptions.find((option) => option.value === value) ||
                booleanOptions[1]
              }
              errors={errors}
              label="APC"
              onChange={(option) => {
                const newValue = option
                  ? (option as BooleanOption).value
                  : false;
                onChange(newValue);
                if (newValue) {
                  // clear MS-DRG fields when switching to APC
                  setValue("ms_drg_code", "", { shouldValidate: true });
                  setValue("ms_drg_description", "", { shouldValidate: true });
                  setValue("ms_drg_base_price", null, { shouldValidate: true });
                } else {
                  // clear facility fee APC CPT and price when switching to MS-DRG
                  setValue("facility_fee_apc_cpt", null, {
                    shouldValidate: true,
                  });
                  setValue("ms_drg_base_price", null, {
                    shouldValidate: true,
                  });
                }
              }}
            />

            {value ? (
              <Controller
                name="facility_fee_apc_cpt"
                control={control}
                rules={{
                  validate: (value) =>
                    (value !== null && value !== undefined) ||
                    "Select a CPT code for APC",
                }}
                render={({ field: { onChange, value, ...field } }) => (
                  <Select
                    {...field}
                    options={cptCodes.map((cpt, index) => ({
                      label: `${cpt.code} - ${cpt.description}`,
                      value: index,
                    }))}
                    value={
                      value !== null
                        ? ({
                            label: `${cptCodes[value]?.code} - ${cptCodes[value]?.description}`,
                            value,
                          } as APCCPTOption)
                        : null
                    }
                    errors={errors}
                    label="APC / CPT"
                    placeholder={
                      cptCodes.length === 0
                        ? "Add a CPT code above"
                        : "Select a CPT code for APC"
                    }
                    readOnly={cptCodes.length === 0}
                    onChange={(option) => {
                      onChange(option ? (option as APCCPTOption).value : null);
                      // only update price when CPT is explicitly selected (not on initial load)
                      if (option) {
                        setValue(
                          "ms_drg_base_price",
                          cptCodes[(option as APCCPTOption).value].price,
                          {
                            shouldValidate: true,
                            shouldDirty: true,
                          },
                        );
                      }
                    }}
                  />
                )}
              />
            ) : (
              <>
                <Controller
                  name="ms_drg_code"
                  control={control}
                  render={({ field: { onChange, value, ...field } }) => (
                    <Select
                      {...field}
                      options={codeOptions}
                      value={
                        value
                          ? ({
                              label: `${value} - ${control._formValues.ms_drg_description}`,
                              value,
                              code: value,
                              description:
                                control._formValues.ms_drg_description,
                            } as DRGCodeOption)
                          : null
                      }
                      errors={errors}
                      label="MS-DRG Code"
                      noOptionsMessage={({ inputValue }) =>
                        !inputValue
                          ? "Begin typing a DRG code..."
                          : isCodeFetching
                            ? "Searching..."
                            : "No matches found."
                      }
                      onInputChange={(value: string) =>
                        debouncedSetCodeSearchTerm(value)
                      }
                      onChange={(option: DRGCodeOption | null) => {
                        if (!option) {
                          onChange("");
                          setValue("ms_drg_description", "", {
                            shouldValidate: true,
                          });
                          return;
                        }
                        onChange(option.value);
                        setValue(
                          "ms_drg_description",
                          option.description || "",
                          {
                            shouldValidate: true,
                          },
                        );
                      }}
                    />
                  )}
                />

                <Controller
                  name="ms_drg_description"
                  control={control}
                  render={({ field: { onChange, value, ...field } }) => (
                    <Select
                      {...field}
                      options={descOptions}
                      value={
                        value
                          ? ({
                              label: value,
                              value: control._formValues.ms_drg_code,
                              code: control._formValues.ms_drg_code,
                              description: value,
                            } as DRGCodeOption)
                          : null
                      }
                      errors={errors}
                      label="MS-DRG Description"
                      noOptionsMessage={({ inputValue }) =>
                        !inputValue
                          ? "Begin typing a description..."
                          : isDescFetching
                            ? "Searching..."
                            : "No matches found."
                      }
                      onInputChange={(value: string) =>
                        debouncedSetDescSearchTerm(value)
                      }
                      onChange={(option: DRGCodeOption | null) => {
                        if (!option) {
                          onChange("");
                          setValue("ms_drg_code", "", { shouldValidate: true });
                          return;
                        }
                        onChange(option.description || "");
                        setValue("ms_drg_code", option.code, {
                          shouldValidate: true,
                        });
                      }}
                    />
                  )}
                />
              </>
            )}
          </>
        )}
      />

      <Controller
        name="ms_drg_base_price"
        control={control}
        rules={{
          validate: (value) => {
            if (value !== null && (isNaN(value) || value < 0)) {
              return "MS-DRG base price must be a positive number";
            }
            return true;
          },
        }}
        render={({ field: { value, onChange, ...field } }) => (
          <MaskedInput
            {...field}
            mask="dollar"
            value={value || ""}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              const rawValue = e.target.value.replace(/[^0-9.]/g, "");
              onChange(rawValue ? Number(rawValue) : null);
            }}
            label={adjApcPayment ? "APC Price" : "MS-DRG Base Price"}
            errors={errors}
          />
        )}
      />
    </>
  );
};

export default FacilityFeeSection;
